import React, { useRef, useState } from 'react'
import { css } from '@emotion/core'
import { string } from 'prop-types'
import {
  HeaderBlock,
  DialogOverlay,
  useOverlay,
  Grid,
  Cell,
  Card,
  useAtSize,
  Spinner,
  Button,
  useStatusOverlay,
} from '@fs/zion-ui'
import { trackEvent } from '@fs/zion-analytics'
import { ContentAdd } from '@fs/zion-icon'
import { useUser } from '@fs/zion-user'
import { useTranslation } from 'react-i18next'
import filterMemories from '../../../../../helpers/memory_helper'
import DSRenderWithMode from '../../../../../helpers/DSRenderWithMode'
import { usePostDocumentMemories, useDeleteMemory } from '../../../hooks/postHooks'
import useMemories from '../../../../../hooks/useMemories'
import handleFileUploadAttempt from '../../../../../helpers/upload'
import DeleteMemoryButton from '../../../../DeleteMemoryButton/DeleteMemoryButton'
import { usePersonViewerState } from '../../../state/PersonViewerStore'

const formCSS = css`
  display: none;
`
const cssAddMemory = css`
  border: 4px dotted #e0e0e0;
  margin: 10px 20px;
  cursor: pointer;
  font-size: 14px;
  color: #999;
  vertical-align: top;
  width: 200px;
  height: 200px;
  align-self: center;
  background-color: #fff;
`

const cssImage = css`
  width: 100%;
  height: 275px;
  outline: none;
  object-fit: cover;
  object-position: center top;
`
const imageFrameCSS = css`
  min-width: 80vw;
  height: 70vh;
`

const documentTitleCSS = css`
  font-weight: bold;
  margin-bottom: 10px;
  padding: 5px;
`

const gridContainerCSS = css`
  margin: 20px;
`

const spinnerContainer = css`
  margin: auto 0;
`

export default function PersonDocuments({ personId, gender, centerOrStation }) {
  const [t] = useTranslation()
  const user = useUser()
  const memoryType = 'document'
  const [isLoading, setIsLoading] = React.useState(true)
  const [currentDocs, setMemories] = React.useState([])
  const uploadForm = useRef(null)
  const uploadTrigger = useRef(null)
  const opener = useOverlay()
  const showStatusOverlay = useStatusOverlay()
  const atSize = useAtSize()
  const [selectedDocumentURL, setSelectedDocumentURL] = useState('')
  const [selectedDocumentTitle, setSelectedDocumentTitle] = useState('')
  const [newDoc, postNewDoc] = usePostDocumentMemories()
  const [memoriesState, dispatch] = useMemories({ personId })
  const [oldMemory, deleteMemory] = useDeleteMemory()
  const personViewerState = usePersonViewerState()
  const { lockMemories } = personViewerState

  const banner = css`
    width: 80%;
    background-color: #fff;
    text-align: center;
    margin: 0 auto;
    padding-bottom: 5px;
    margin-top: ${atSize({ default: '75px', xxl: '200px' })};
  `

  const bannerBtn = css`
    display: 'flex';
    flex-direction: 'column';
    align-items: 'center';
    margin-top: 20px;
  `

  React.useEffect(() => {
    if (memoriesState.complete && memoriesState.res && memoriesState.res.sourceDescriptions) {
      const images = filterMemories({ memories: memoriesState.res, memoryType: 'application/pdf' })
      setMemories(images)
      setIsLoading(false)
    } else if (memoriesState.complete && memoriesState.res === '') {
      setIsLoading(false)
    }
  }, [memoriesState])

  React.useEffect(() => {
    if (newDoc.pending) {
      showStatusOverlay({
        message: t(`person-details.uploading-memory-${memoryType}`),
        type: 'normal',
        dismissable: true,
      })
    }
  }, [newDoc.pending, showStatusOverlay, t])

  React.useEffect(() => {
    if (newDoc.complete && !newDoc.error && !newDoc.pending) {
      dispatch({ type: 'load' })
      showStatusOverlay({
        message: t(`person-details.upload-successful-${memoryType}`),
        type: 'success',
        dismissable: true,
      })
    }
  }, [dispatch, newDoc, showStatusOverlay, t])

  React.useEffect(() => {
    if (newDoc.complete && newDoc.error && !newDoc.pending) {
      showStatusOverlay({
        message: t(
          `person-details.status-${newDoc.error?.response?.status || 'error'}${
            newDoc.error?.response?.status === 409 ? `-${memoryType}` : ''
          }`
        ),
        type: 'error',
        dismissable: true,
      })
    }
  }, [newDoc, showStatusOverlay, t])

  React.useEffect(() => {
    if (oldMemory.complete && !oldMemory.error && !oldMemory.pending) {
      dispatch({ type: 'load' })
    }
  }, [dispatch, oldMemory])

  const upload = () => {
    const fd = new FormData(uploadForm.current)

    postNewDoc({
      ancestorId: personId,
      data: fd,
    })
  }

  const uploadDocument = () => {
    uploadTrigger.current.click()
    trackEvent({ event_name: 'Document Upload' })
  }

  const openDialog = ({ title, link }) => {
    setSelectedDocumentURL(link)
    setSelectedDocumentTitle(title)
    opener.open()
  }

  const handleFileUploadOnChange = () => {
    handleFileUploadAttempt({
      fileForm: uploadForm.current,
      fileInputName: 'story',
      uploadCallback: upload,
      fileTooLargeHandler: () => {
        showStatusOverlay({
          message: t('person-details.file-too-large', 'The file is too large.  The size limit is 15 megabytes.'),
          type: 'error',
        })
      },
    })
  }

  return (
    <>
      {!isLoading && currentDocs.length > 0 && (
        <div>
          <DialogOverlay headingText={selectedDocumentTitle} {...opener} autoWidth>
            <iframe id="pdf-frame" title="View PDF" css={imageFrameCSS} src={selectedDocumentURL} width="100%" />
          </DialogOverlay>
          <div css={gridContainerCSS}>
            <Grid>
              {!lockMemories && (
                <DSRenderWithMode allowedModes={['WEB']}>
                  <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key="add-button-cell">
                    <button css={cssAddMemory} type="button" onClick={uploadDocument}>
                      <ContentAdd size="xl" color="#606060" />
                      <div>{t('person-details.upload-document', 'Upload document')}</div>
                    </button>
                  </Cell>
                </DSRenderWithMode>
              )}
              {currentDocs.map((item) => (
                <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })} key={item.id}>
                  <Card
                    onKeyPress={() => openDialog({ title: item.title, link: item.link })}
                    onClick={() => openDialog({ title: item.title, link: item.link })}
                    role="button"
                    tabIndex={-1}
                    data-link={item.link}
                  >
                    <Card.Body>
                      <span css={documentTitleCSS}>{item.title}</span>
                      <img css={cssImage} src={item.thumb} loading="lazy" alt=" " />
                    </Card.Body>
                    {item.resourceId === user.cisId && !lockMemories && !centerOrStation && (
                      <Card.Actions>
                        <DeleteMemoryButton artifactId={item.id} deleteMemory={deleteMemory} />
                      </Card.Actions>
                    )}
                  </Card>
                </Cell>
              ))}
              {(newDoc.pending || oldMemory.pending) && (
                <Cell columns={atSize({ default: 12, sm: 6, lg: 4, xl: 3, xxl: 2 })}>
                  <div css={spinnerContainer}>
                    <Spinner large />
                  </div>
                </Cell>
              )}
            </Grid>
          </div>

          <div css={formCSS}>
            <form ref={uploadForm} id="document-form" method="post" name="documentForm">
              <input
                ref={uploadTrigger}
                name="story"
                onChange={handleFileUploadOnChange}
                type="file"
                accept="application/pdf"
              />
            </form>
          </div>
        </div>
      )}

      {!isLoading && currentDocs.length === 0 && (
        <div css={banner}>
          {lockMemories && <HeaderBlock size="md" heading={t('person-details.no-records', 'No records found')} />}
          {centerOrStation && (
            <HeaderBlock
              size="md"
              heading={t('person-details.be-first-center', 'Be the first to add to this record on FamilySearch.org')}
            />
          )}
          {!lockMemories && !centerOrStation && (
            <div>
              <div css={formCSS}>
                <form ref={uploadForm} id="document-form" method="post" name="documentForm">
                  <input
                    ref={uploadTrigger}
                    name="story"
                    onChange={handleFileUploadOnChange}
                    type="file"
                    accept="application/pdf"
                  />
                </form>
              </div>
              <HeaderBlock size="md" heading={t('person-details.be-first', 'Be the first to add to this record')} />
              <div css={bannerBtn}>
                <Button
                  emphasis="billboard"
                  color={gender && gender === 'male' ? 'blue' : 'red'}
                  onClick={uploadDocument}
                >
                  {t('person-details.upload-document', 'Upload document')}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
      {isLoading && currentDocs.length === 0 && (
        <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '500px' }}>
          <Spinner large />
        </div>
      )}
    </>
  )
}

PersonDocuments.propTypes = {
  personId: string,
}
